import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { AuthShell } from "@/components/shared/AuthShell";
import { useNeutralAuthCover } from "@/hooks/useNeutralAuthCover";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { PasswordStrengthIndicator } from "@/components/shared/PasswordStrengthIndicator";
import { resetPasswordWithToken } from "@/services/authService";
import toast from "react-hot-toast";

export function ResetPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const cover = useNeutralAuthCover();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t("auth.resetPassword.mismatch", "Passwords do not match."));
      return;
    }
    if (!token) {
      toast.error(
        t("auth.resetPassword.invalidOrExpired", "Link invalid or expired. Request a new reset link.")
      );
      return;
    }
    setIsSubmitting(true);
    try {
      await resetPasswordWithToken(token, newPassword);
      toast.success(t("auth.resetPassword.success", "Password updated. Sign in with your new password."));
      navigate("/login", { replace: true });
    } catch {
      toast.error(
        t("auth.resetPassword.invalidOrExpired", "Link invalid or expired. Request a new reset link.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell cover={cover}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-bold text-foreground">
              {t("auth.resetPassword.title", "Reset your password")}
            </h1>
            <p className="text-balance text-muted-foreground">
              {t("auth.resetPassword.invalidOrExpired", "Link invalid or expired. Request a new reset link.")}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Button asChild variant="outline" className="w-full">
              <Link to="/auth/forgot-password">
                {t("auth.resetPassword.requestNew", "Request new link")}
              </Link>
            </Button>
            <Button asChild variant="ghost" className="w-full">
              <Link to="/login">{t("auth.resetPassword.backToLogin", "Back to login")}</Link>
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell cover={cover}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t("auth.resetPassword.title", "Reset your password")}
          </h1>
          <p className="text-balance text-muted-foreground">
            {t("auth.resetPassword.subtitle", "Enter your new password below.")}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label htmlFor="newPassword">
              {t("auth.resetPassword.newPassword", "New password")}
            </Label>
            <PasswordInput
              id="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              required
            />
            <PasswordStrengthIndicator
              password={newPassword}
              showRules
              rulesLabels={{
                minLength: t("auth.register.passwordStrength.minLength"),
                uppercase: t("auth.register.passwordStrength.uppercase"),
                number: t("auth.register.passwordStrength.number"),
                special: t("auth.register.passwordStrength.special"),
              }}
              strengthLabels={{
                weak: t("auth.register.passwordStrength.weak"),
                fair: t("auth.register.passwordStrength.fair"),
                good: t("auth.register.passwordStrength.good"),
                strong: t("auth.register.passwordStrength.strong"),
              }}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="confirmPassword">
              {t("auth.resetPassword.confirmPassword", "Confirm password")}
            </Label>
            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={8}
              required
            />
          </div>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t("auth.resetPassword.submitting", "Updating…")
              : t("auth.resetPassword.submit", "Update password")}
          </Button>
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">{t("auth.resetPassword.backToLogin", "Back to login")}</Link>
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
